import {
    ChooseDropdown,
    DropdownButtonTrigger,
    Flex,
    PageNav,
    Scrollbar
} from '@priority-software/priority-style-react';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import SearchInput from '../../../ReportGenerator/componenets/SearchInput';
import styles from './styles.module.scss';
import {isRTL} from '../../../../Localization';
import HeaderCol from '../../../ReportGenerator/componenets/ReportList/HeaderCol';
import {createDataTestId} from '../../../../utils/dataTestId';
import {ITEMS_PER_PAGE} from '../../../ReportGenerator/componenets/ReportList/consts';
import {filterAndSort} from '../../../ReportGenerator/componenets/ReportList/utils';
import texts from '../../../ReportGenerator/texts';
import {analyticsReportsStore} from '../../store/AnalyticsReportsStore';
import {ANALYTICS_REPORT_DIALOG, getHeaderColumns} from '../../constants';
import {ReportColumns} from '../../../ReportGenerator/componenets/ReportList/ReportColumns';
import {observer} from 'mobx-react-lite';
import {NoMatchEmptyState} from '../../../ReportGenerator/componenets/ReportList/EmptyState';
import {SelectedItem} from '../../types';
import {filterReportsByUserAndForms} from '../../utils';
import {keys} from '../../../../constants/KeyCodes';
import {SearchTags} from '../SearchTags/SearchTags';
import {BoldItem} from '../../../ReportGenerator/componenets/ReportList/types';

export const ReportsContent = observer(() => {
    const {
        reportsByType,
        sort,
        currentNavPage,
        setSort,
        setSearchQuery,
        listSearchQuery,
        setSelectedReport,
        selectedReport,
        setNavPage,
        users,
        basedForms,
        selectedUsers,
        selectedForms,
        setSelectedForms,
        setSelectedUsers
    } = analyticsReportsStore;

    const scrollbarRef = useRef(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const rtl = isRTL();

    const filteredList = useMemo(() => {
        const filteredReports = filterReportsByUserAndForms(
            reportsByType,
            selectedUsers,
            selectedForms
        );
        return filterAndSort(listSearchQuery, sort, filteredReports);
    }, [listSearchQuery, reportsByType, sort, selectedUsers, selectedForms]);

    const startIndex = ITEMS_PER_PAGE * (currentNavPage - 1);
    const endIndex = startIndex + ITEMS_PER_PAGE;

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (
                e.key === keys.ENTER &&
                (document.activeElement as HTMLInputElement)?.name === 'radio'
            ) {
                (document.activeElement as HTMLInputElement).click();
            }
        };

        containerRef.current?.addEventListener('keydown', handler);
        return () => containerRef.current?.removeEventListener('keydown', handler);
    }, []);

    useEffect(() => {
        requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
    }, []);

    const handleSort = (id: string) => () => {
        const isActive = sort.id === id;
        const isDesc = isActive ? !sort.isDesc : false;
        setSort({id, isDesc});
    };

    const headersColumns = useMemo(
        () =>
            getHeaderColumns().map(item => ({
                colId: item.id,
                title: item.title,
                isSortactive: sort?.id === item.id,
                onSort: handleSort(item.id),
                isDesc: sort.isDesc,
                testId: item.id
            })),
        [sort]
    );

    const handleSetPage = useCallback(
        (nextPage: number) => {
            setNavPage(nextPage);
            scrollbarRef?.current?.scrollToTop();
        },
        [setNavPage]
    );

    const handleSelectUsers = useCallback(
        (data: SelectedItem, isSelected: boolean, name: string) => {
            if (isSelected) {
                setSelectedUsers([...selectedUsers, data.value]);
            } else {
                setSelectedUsers(selectedUsers.filter(user => user !== data.value));
            }
        },
        [setSelectedUsers, selectedUsers]
    );

    const handleSelectForms = useCallback(
        (data: SelectedItem, isSelected: boolean, name: string) => {
            if (isSelected) {
                setSelectedForms([...selectedForms, data.value]);
            } else {
                setSelectedForms(selectedForms.filter(form => form !== data.value));
            }
        },
        [setSelectedForms, selectedForms]
    );

    return (
        <Flex
            justifyContent="flex-start"
            style={{
                marginBottom: '16px',
                height: '100%',
                flex: 1
            }}
            direction="column"
        >
            <Flex justifyContent="space-between" style={{marginBottom: '16px'}}>
                <SearchInput
                    inputRef={inputRef}
                    searchQuery={listSearchQuery}
                    onChangeQuery={setSearchQuery}
                    inputClasses={styles.input}
                    roundedCorners
                    placeholder={texts.search}
                />
                <Flex justifyContent="flex-end" style={{width: 'fit-content', gap: '8px'}}>
                    <ChooseDropdown
                        isMulti
                        isRtl={rtl}
                        itemBorder={false}
                        width={150}
                        onChange={handleSelectUsers}
                        value={selectedUsers}
                        trigger={<DropdownButtonTrigger content={texts.createdBy} />}
                        data={users}
                        noOptionsText={texts.no_options_text}
                        usePortal
                        portalSelector={ANALYTICS_REPORT_DIALOG}
                        defaultFocusedIndex={0}
                        maxScrollHeight={320}
                        searchPlaceholder={texts.search}
                    />
                    <ChooseDropdown
                        isMulti
                        isRtl={rtl}
                        itemBorder={false}
                        width={300}
                        onChange={handleSelectForms}
                        value={selectedForms}
                        trigger={<DropdownButtonTrigger content={texts.baseForm} />}
                        data={basedForms}
                        noOptionsText={texts.no_options_text}
                        usePortal
                        portalSelector={ANALYTICS_REPORT_DIALOG}
                        defaultFocusedIndex={0}
                        maxScrollHeight={320}
                        searchPlaceholder={texts.search}
                        openingSide="leftSide"
                    />
                </Flex>
            </Flex>
            <SearchTags />

            <div className={styles.tableContainer} ref={containerRef}>
                {filteredList.length > 0 ? (
                    <>
                        <div className={styles.table}>
                            <div className={styles.tableHeader}>
                                <div className={styles.tableHeaderItem}></div>
                                {headersColumns.map(col => (
                                    <HeaderCol
                                        key={col.colId}
                                        title={col.title}
                                        isSortActive={col.isSortactive}
                                        onSort={col.onSort}
                                        isDesc={col.isDesc}
                                        testId={col.testId}
                                    />
                                ))}
                            </div>
                            <Scrollbar
                                scrollbarRef={scrollbarRef}
                                rtl={isRTL()}
                                autoHide={false}
                                autoHeightMin={120}
                            >
                                <div className={styles.tableBody}>
                                    {filteredList.slice(startIndex, endIndex).map(report => (
                                        <div
                                            key={report.id as number}
                                            className={styles.tableRow}
                                            {...createDataTestId('report-list-row', false)}
                                            onClick={() => setSelectedReport(report.id as number)}
                                        >
                                            <ReportColumns
                                                boldItem={BoldItem.title}
                                                report={report}
                                                searchQuery={listSearchQuery}
                                                isAnalyticsReportDialog
                                                onSelect={setSelectedReport}
                                                isSelected={report.id === selectedReport?.id}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </Scrollbar>
                        </div>
                        <div style={{height: '72px'}}>
                            <PageNav
                                rtl={rtl}
                                activePage={currentNavPage}
                                pagesCount={Math.ceil(filteredList.length / ITEMS_PER_PAGE)}
                                onItemClick={handleSetPage}
                                {...createDataTestId('analytics-report-list-pageNav', true)}
                                pageNavItemDataId
                            />
                        </div>
                    </>
                ) : (
                    <NoMatchEmptyState
                        componentClasses={styles.emptyList}
                        text={texts.emptyResult}
                    />
                )}
            </div>
        </Flex>
    );
});
